/**
 * Auth schema version gate.
 *
 * Bump SCHEMA_VERSION whenever the auth contract on disk changes in a way
 * that would confuse old clients: cookie name change, csrf storage layout,
 * sentinel keys, OIDC settings, etc.
 *
 * On the first boot of a new SCHEMA_VERSION:
 *   1. Best-effort POST /auth/user/logout (clears server-side cookies).
 *   2. localStorage.clear()  — drops oidc.user.*, csrf, refresh state, sentinels.
 *   3. sessionStorage.clear() — drops per-tab oidc_redirect, logout flags.
 *   4. Stamp the new version so we don't loop on subsequent boots.
 *
 * Effect: after a deploy that bumps this version, every tab on every machine
 * is guaranteed to start from a clean slate without users having to "vider
 * les données de navigation" by hand. They just see /auth/login and re-auth.
 */
const SCHEMA_VERSION = '2026-05-03-auth-refacto'
const VERSION_KEY = 'app_auth_schema_version'

/**
 * Defensive `localStorage.clear()` that survives the auth schema gate stamp.
 * Anything blowing away localStorage on logout MUST go through this — wiping
 * VERSION_KEY makes the next top-level navigation re-trigger the gate, which
 * fires an extra POST /logout and clears sessionStorage (losing oidc_redirect).
 */
export function clearLocalStoragePreservingSchema(): void {
  if (typeof window === 'undefined') return
  let stamp: string | null = null
  try { stamp = localStorage.getItem(VERSION_KEY) } catch { /* ignore */ }
  try { localStorage.clear() } catch { /* ignore */ }
  if (stamp) {
    try { localStorage.setItem(VERSION_KEY, stamp) } catch { /* ignore */ }
  }
}

export async function runAuthSchemaGate(): Promise<void> {
  if (typeof window === 'undefined') return

  const stored = localStorage.getItem(VERSION_KEY)
  if (stored === SCHEMA_VERSION) return

  // Best-effort server-side logout — also clears the httpOnly core_session
  // cookie that JS can't touch. Failure is fine (user wasn't logged in,
  // network down, server restarting, …).
  try {
    const url = (import.meta.env.VITE_SUITE_URL as string) + '/auth/user/logout'
    await fetch(url, { method: 'POST', credentials: 'include' })
  } catch {
    /* ignore */
  }

  try { localStorage.clear() } catch { /* ignore */ }
  try { sessionStorage.clear() } catch { /* ignore */ }

  // Stamp the new version *after* the cleanup so a crash mid-cleanup retries.
  try { localStorage.setItem(VERSION_KEY, SCHEMA_VERSION) } catch { /* ignore */ }
}
